<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Create Blog Post</h1>
        </div>

        <div class="modal-body no-pad" v-if="post">
            <div class="flex h-full">
                <div class="account-sidebar h-full">
                    <div class="mb-4 text-center">
                        <span class="fa-layers fa-4x mb-3">
                            <i class="fa fa-circle"></i>
                            <i class="fas fa-user fa-inverse" data-fa-transform="shrink-6.5"></i>
                        </span>

                        <p class="text-lg font-light"></p>
                    </div>

                    <div class="mb-3">
                        <label>ID</label>
                        <p>&infin;</p>
                    </div>
                </div>

                <div class="account-body">
                    <form class="profile-form">
                        <div class="form-field">
                            <input type="text" v-model="post.blog_title" @input="createSlug">
                            <label>Title</label>
                        </div>

                        <div class="form-field">
                            <input type="text" v-model="post.blog_slug">
                            <label>URL Slug</label>
                        </div>

                        <div class="form-field">
                            <select v-model="post.blog_category">
                                <option v-for="(category, index) in categories" :key="index" :value="category.cat_id">
                                    {{ category.cat_name }}
                                </option>
                            </select>
                            <label>Category</label>
                        </div>

                        <div class="form-field styled">
                            <label>Publish Date/Time</label>
                            <simple-date-time-picker
                                v-model="post.blog_datetime">
                            </simple-date-time-picker>
                        </div>

                        <div class="form-field">
                            <textarea rows="3" v-model="post.blog_summary"></textarea>
                            <label>Summary Text</label>
                        </div>

                        <div class="form-field styled">
                            <label>Content</label>
                            <Editor
                                :init="{
                                    ...wysiwygOptions,
                                    plugins: wysiwygPlugins
                                }"
                                api-key="kxkhkjfta11crbajh0in2mcap04z7nt4czo9icice5ihpd8n"
                                v-model="post.blog_content"
                            >
                            </Editor>
                        </div>

                        <div class="form-field styled">
                            <label>Original Article URL (if duplicated from elsewhere)</label>
                            <input type="text" v-model="post.blog_canonical">
                            <p class="text-xs text-gray-600 mt-1">If this content is being duplicated from another
                                source, paste the link here to prevent search engines from penalising us for duplicate
                                content.</p>
                        </div>

                        <div class="form-field">
                            <input type="text" v-model="post.blog_meta_title">
                            <label>Meta &amp; Social Title</label>
                        </div>

                        <div class="form-field">
                            <textarea rows="3" v-model="post.blog_meta_description"></textarea>
                            <label>Meta &amp; Social Description</label>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="flex">
                <div class="right text-right">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="saveForm">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'

export default {
    name: 'BlogCreate',
    props: {
        //
    },
    components: {
        Editor,
    },
    data() {
        return {
            post: {
                blog_title: null,
                blog_summary: null,
                blog_content: null,
                blog_slug: null,
                blog_category: null,
                blog_canonical: null,
                blog_meta_description: null,
            },
            categories: [],
        }
    },
    mounted() {
        this.loadData()

        this.setMetaTitle('Create Blog Post | JournoLink Control')
    },
    beforeDestroy() {
        //
    },
    methods: {
        loadData() {
            axios.get(route('blogs.create'))
                .then(response => {
                    this.categories = response.data.categories
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.$root.goBack()
        },
        /**
         *
         */
        saveForm() {
            axios.post(route('blogs.create'), this.formatPostForSave(this.post))
                .then(response => {
                    this.post = response.data.post
                    this.$bus.$emit('alert.success', 'Blog Post has been saved')
                    this.$router.push({name: 'blog.list', params: {newPost: this.post}})
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         * Create a slug from the title
         *
         * @return {String}
         */
        createSlug() {
            this.post.blog_slug = this.post.blog_title.toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9\-]/g, '').substr(0, 100)
        },
        /**
         * Format the post object for save
         *
         * @param  {Object} post
         * @return {Object}
         */
        formatPostForSave(post) {
            let newPost = _.clone(post)
            newPost.blog_datetime = moment(newPost.blog_datetime).format('YYYY-MM-DD HH:mm:ss')

            return newPost
        },
    },
    watch: {
        post: {
            handler(to) {
                // The date/time picker component requires a ISO formatted string, so we'll
                // convert this here, rather than amending the format in the controller
                if (!moment(to.blog_datetime, 'YYYY-MM-DDTHH:mm:ss', true).isValid()) {
                    this.post.blog_datetime = moment(to.blog_datetime).toISOString(true)
                }
            },
            deep: true,
        },
    }
}
</script>

<style lang="less">
.account-sidebar {
    padding: 15px;
    width: 350px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.account-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
}

.hidden-input {
    input {
        display: none;
        width: 100%;
    }

    .show {
        display: inline-block;
    }

    .hide {
        display: none;
    }

    .edit-field-link {
        text-decoration-line: underline;
        text-decoration-style: solid;
        color: #222222;
        word-wrap: break-word;
    }
}

.profile-form {
    .section-title {
        margin-top: 20px;
    }

    .form-title {
        margin-bottom: 20px;
    }
}
</style>
